/** Кэш загрузки глоссария (спек «Загрузка списка», «Реальное время»): ход — точечно, применение — один перечёт. */
import type { QueryClient } from '@tanstack/react-query';
import type { GlossaryData } from '@/api/glossaryModel';
import type { GlossaryImport, ImportProgress } from '@/api/glossaryImportModel';
import { glossaryQueryKey, suggestionsQueryKey } from '../stressCache';

export const glossaryImportQueryKey = (projectId: string, importId: string) => ['glossaryImport', projectId, importId] as const;

export function applyImportProgress(qc: QueryClient, p: ImportProgress): void {
  const key = glossaryImportQueryKey(p.projectId, p.importId);
  // Черновик сервер собирает после ответа на «Загрузить»: окно без черновика перечитывает загрузку на первом событии
  if (qc.getQueryData<GlossaryImport>(key)?.draft === null) void qc.invalidateQueries({ queryKey: key });
  qc.setQueryData<GlossaryImport>(key, (d) => (d ? { ...d, status: p.status } : d));
  // Кнопка возврата в окно и «Разбираем файл…» под заголовком «Глоссарий» — из active_import
  qc.setQueryData<GlossaryData>(glossaryQueryKey(p.projectId), (d) => {
    if (!d) return d;
    if (p.status === 'parsing' || p.status === 'ready') {
      return { ...d, active_import: { import_id: p.importId, status: p.status } };
    }
    return d.active_import?.import_id === p.importId ? { ...d, active_import: null } : d;
  });
}

/**
 * Загрузка применена или отменена: сотни записей разом — глоссарий и предложения перечитываются один раз.
 * Без projectId (чужой проект тронул общий словарь) — перечитываются открытые глоссарии.
 */
export function applyImported(qc: QueryClient, p: { projectId?: string; importId?: string; action: 'applied' | 'reverted'; shared: boolean }): void {
  if (p.projectId) {
    void qc.invalidateQueries({ queryKey: glossaryQueryKey(p.projectId) });
    void qc.invalidateQueries({ queryKey: suggestionsQueryKey(p.projectId) });
    if (p.importId) void qc.invalidateQueries({ queryKey: glossaryImportQueryKey(p.projectId, p.importId) });
    return;
  }
  if (!p.shared) return;
  void qc.invalidateQueries({ queryKey: ['glossary'] });
  void qc.invalidateQueries({ queryKey: ['glossarySuggestions'] });
}
